;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import z from "zod";
import { ApiRequestError } from "@/lib/api/api-request-error";
import {
	deleteLocalStorageItem,
	getLocalStorageItem,
	setLocalStorageItem,
} from "@/lib/browser-storage/localStorage";
import { logger } from "@/lib/services/logger";
import { useAuthStore } from "@/lib/stores/authStore";
import type {
	TrackPageViewRequest,
	TrackPageViewResponse,
} from "@/types/usage-tracking";

const PAGE_ACTIVITY_ACTIONS = {
	EXTERNAL_LINK_NAVIGATION: "external_link_navigation",
	PAGE_NAVIGATION: "page_navigation",
	PAGE_VIEW: "page_view",
	SIGN_IN: "sign_in",
	SIGN_OUT: "sign_out",
	SIGN_UP: "sign_up",
} as const;

const SESSION_ACTIVITY_KEY = "session-activity-id";

const sessionActivitySchema = z.object({
	email: z.email({ message: "Invalid email address" }).toLowerCase().trim(),
	organizationId: z.string().optional(),
	pageActivity: z
		.array(
			z.object({
				action: z.string().min(1),
				payload: z.record(z.string(), z.any()),
				time: z.iso.datetime(),
			}),
		)
		.optional(),
	pagePath: z.string().min(1, { error: "Page path is required" }),
	sessionId: z.string().optional(),
	userId: z.string().optional(),
	workspaceId: z.string().optional(),
});

export class SessionActivity {
	private sessionEpoch = 0;
	private sessionId: string;
	private static instance: SessionActivity;
	public static pageActivityActions = PAGE_ACTIVITY_ACTIONS;

	private constructor() {
		this.sessionId = this.getStoredSessionId();
	}

	private getStoredSessionId(): string {
		return getLocalStorageItem(SESSION_ACTIVITY_KEY);
	}

	get headers(): Headers {
		return new Headers({
			"Content-Type": "application/json",
			"x-session-id": this.sessionId,
		});
	}

	get derivedProperties(): TrackPageViewRequest {
		return {
			email: useAuthStore.getState().user?.email ?? "",
			organizationId: useAuthStore.getState().user?.organizationId ?? "",
			pageActivity: [],
			pagePath: typeof window !== "undefined" ? window.location.pathname : "",
			sessionId: this.sessionId,
			userId: useAuthStore.getState().user?.id ?? "",
		};
	}

	private async storeSessionId(sessionId: string): Promise<void> {
		const previousSessionId = this.sessionId;
		const replacesExistingSession =
			previousSessionId.length > 0 && previousSessionId !== sessionId;

		setLocalStorageItem(SESSION_ACTIVITY_KEY, sessionId);
		this.sessionId = sessionId;

		if (replacesExistingSession) {
			await this.endTrackedSession(previousSessionId);
		}
	}

	async recordActivity(
		properties: Partial<TrackPageViewRequest>,
	): Promise<void> {
		const epochAtStart = this.sessionEpoch;
		const sessionIdAtStart = this.sessionId;
		const validatedProperties = sessionActivitySchema.safeParse({
			...this.derivedProperties,
			...properties,
		});

		if (!validatedProperties.success) {
			logger.warn("Failed to validate activity properties", {
				feature: "session-activity",
				operation: "recordActivity",
				sessionId: sessionIdAtStart,
				validationError: z.flattenError(validatedProperties.error),
			});
			return;
		}

		const request = await fetch(`/api/usage-tracking`, {
			body: JSON.stringify(validatedProperties.data),
			headers: this.headers,
			method: "POST",
		});

		const response = await request.json();

		if (epochAtStart !== this.sessionEpoch) {
			return;
		}

		if (!request.ok) {
			logger.captureUnexpected(
				"Failed to record activity",
				new ApiRequestError({
					message: JSON.stringify(response),
					status: request.status,
				}),
				{
					feature: "session-activity",
					operation: "recordActivity",
					sessionId: sessionIdAtStart,
				},
			);
			return;
		}

		await this.storeSessionId((response as TrackPageViewResponse).sessionId);
	}

	async endSession(): Promise<void> {
		const sessionId = this.sessionId;
		if (!sessionId) {
			return;
		}

		this.sessionEpoch += 1;
		this.sessionId = "";
		deleteLocalStorageItem(SESSION_ACTIVITY_KEY);
		await this.endTrackedSession(sessionId);
	}

	private async endTrackedSession(sessionId: string): Promise<void> {
		try {
			const request = await fetch(
				`/api/usage-tracking?sessionId=${sessionId}`,
				{
					headers: new Headers({
						"Content-Type": "application/json",
						"x-session-id": sessionId,
					}),
					method: "DELETE",
				},
			);

			const response = await request.json();

			if (!request.ok) {
				logger.captureUnexpected(
					"Failed to end session",
					new ApiRequestError({
						message: JSON.stringify(response),
						status: request.status,
					}),
					{
						feature: "session-activity",
						operation: "endSession",
						sessionId,
					},
				);
			}
		} catch (error) {
			logger.captureUnexpected(
				"Failed to end session",
				error instanceof Error ? error : new Error("Failed to end session"),
				{
					feature: "session-activity",
					operation: "endSession",
					sessionId,
				},
			);
		}
	}

	static getInstance(): SessionActivity {
		if (!SessionActivity.instance) {
			SessionActivity.instance = new SessionActivity();
		}

		return SessionActivity.instance;
	}
}

export const sessionActivityHandler = SessionActivity.getInstance();
